/**
 * Small (i) icon that reveals a long description on hover (desktop) or tap
 * (touch, which has no hover). Same control as the one on the Section
 * header, one level down — shared here so a Field's long hint gets the same
 * accessible/tap-target behavior without redriving those decisions.
 */
import { useState } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function InfoTooltip({
  description,
  className,
}: {
  description: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          /* The description is the button's accessible name: the icon
             carries no text, and naming it this way keeps the copy
             reachable without a hover and without inventing a second
             string to translate. Our TooltipContent wrapper spreads props
             after Radix's own, so a manual id/aria-describedby pair here
             would silently overwrite Radix's internal wiring instead of
             extending it — aria-label sidesteps that entirely. */
          aria-label={description}
          onClick={(e) => {
            // Hover/focus already show the tooltip on desktop; a click lets
            // touch devices, which have no hover, open it too.
            e.preventDefault();
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
          /* Negative margin keeps the icon where it looks right while
             padding gives the tap target real size. */
          className={
            className ??
            "-m-1.5 shrink-0 p-1.5 text-muted-foreground/60 hover:text-muted-foreground"
          }
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">
        {description}
      </TooltipContent>
    </Tooltip>
  );
}
